import { useEffect, useRef, useState, type ReactElement, type ReactNode, type Ref } from 'react';
import { toast } from '../../../../design-system/components';
import { track, trackFailed } from '../../../../../../shared-web/track';
import { ApiError } from '../../../auth/api-client';
import { apiErrorCode, wordsRefused, type GroupCheck, type GroupEmoji, type GroupFace, type GroupReaction, type GroupWritingKind } from '../../api';
import { Link } from '../../components/Link';
import { PageBack } from '../../components/PageBack';
import { RoomReactionBar } from '../../components/RoomReactions';
import { capsuleChoice } from './group-reactions';
import { LIVE_GROUPS_EVENT } from '../../live';
import { BACK_CHEVRON, Face, FriendsBar, dayLabel, fitHeight } from '../friends/friend-parts';
import './groups.css';

/**
 * What the Groups pages share (2026-11-03 founder; edge peer-groups.js): a face as a group shows it (Ember's is the
 * flame), when something went up, the live re-read, the mic beside a field, Ember's look before anything goes up, and
 * the words held back with the crisis lines. The pages wear Meet people's paper (`.nf-page`, new-friends.css).
 */

/** The writing limits (edge config.js GROUPS). */
export const GROUP_LIMITS = Object.freeze({ QUESTION: 200, BODY: 4000, ANSWER: 2000 });

/** A Groups page's bar: Back (or the given control) on the left, a title in the middle, a control on the right. */
export function GroupBar({ to, left, title, right }: { to: string; left?: ReactNode; title?: string; right?: ReactNode }): ReactElement {
  return (
    <header className="nf-bar">
      {left ?? <PageBack to={to} className="nf-back" />}
      <div className="nf-bar-mid">{title ? <span className="nf-bar-title gr-bar-title" dir="auto">{title}</span> : null}</div>
      {right ?? <span className="nf-bar-end" aria-hidden />}
    </header>
  );
}

/** A face in a group: a member's room picture, or Ember's flame. */
export function GroupFaceView({ face, size, className = '' }: { face: GroupFace; size: number; className?: string }): ReactElement {
  if (face.ember) {
    return (
      <span className={`gr-ember-face${className ? ` ${className}` : ''}`} style={{ width: size, height: size }} aria-hidden>
        <svg viewBox="0 0 24 24" width={Math.round(size * 0.5)} height={Math.round(size * 0.5)}>
          <path d="M12 3c3 4.5 6 7 6 11a6 6 0 0 1-12 0c0-4 3-6.5 6-11z" fill="#fffaf2" />
        </svg>
      </span>
    );
  }
  return <Face avatar={face.avatar || ''} size={size} className={className} />;
}

/** Faces overlapping in a row, each ringed in the ground's colour. */
export function FaceStack({ faces, size, you = false }: { faces: GroupFace[]; size: number; you?: boolean }): ReactElement {
  return (
    <span className="gr-stack" aria-hidden>
      {faces.map((f, i) => <GroupFaceView key={`${f.name}-${i}`} face={f} size={size} className="gr-stack-face" />)}
      {you && <span className="gr-stack-you" style={{ width: size, height: size }}>you</span>}
    </span>
  );
}

/** When something went up: "just now", "40m", "3h", "yesterday", else the day. */
export function ago(iso: string | null | undefined): string {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const mins = Math.floor(Math.max(0, Date.now() - t) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m`;
  if (mins < 24 * 60) return `${Math.floor(mins / 60)}h`;
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  if (new Date(t).toDateString() === yesterday.toDateString()) return 'yesterday';
  return dayLabel(iso);
}

/** A group's own pages read again when the person comes back to the tab, and when the live channel says a group changed. */
export function useGroupsLive(read: () => void): void {
  const ref = useRef(read);
  ref.current = read;
  useEffect(() => {
    const back = (): void => { if (document.visibilityState === 'visible') ref.current(); };
    // Only while on screen: a post's page read is a look (its answers count as read, and its writer is not told of them),
    // so a tab left in the background reads again when they come back to it.
    const changed = (): void => { if (document.visibilityState === 'visible') ref.current(); };
    document.addEventListener('visibilitychange', back);
    window.addEventListener(LIVE_GROUPS_EVENT, changed);
    return () => {
      document.removeEventListener('visibilitychange', back);
      window.removeEventListener(LIVE_GROUPS_EVENT, changed);
    };
  }, []);
}

// ───────────────────────── the mic beside a field ─────────────────────────

interface SpeechResults { length: number; [i: number]: { 0: { transcript: string } } }
interface Recognition {
  continuous: boolean;
  interimResults: boolean;
  onresult: ((e: { results: SpeechResults }) => void) | null;
  onerror: ((e: { error: string }) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
  abort: () => void;
}
type RecognitionCtor = new () => Recognition;

function recognitionCtor(): RecognitionCtor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as { SpeechRecognition?: RecognitionCtor; webkitSpeechRecognition?: RecognitionCtor };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export interface Dictation {
  /** The browser can turn speech into words (no mic is drawn where it cannot). */
  supported: boolean;
  /** The field listening now, or null. */
  listening: string | null;
  start: (field: string, typed: string, onText: (text: string) => void) => void;
  stop: () => void;
}

/**
 * Saying it instead of typing it (typing first, the mic second): the browser's own speech recognition, the words
 * landing in the field after what was typed, live as they are heard. Nothing is sent anywhere by us; the field is theirs
 * to read and change before Next.
 */
export function useDictation(): Dictation {
  const rec = useRef<Recognition | null>(null);
  const [listening, setListening] = useState<string | null>(null);
  useEffect(() => () => { rec.current?.abort(); rec.current = null; }, []);

  const start = (field: string, typed: string, onText: (text: string) => void): void => {
    const Ctor = recognitionCtor();
    if (!Ctor) return;
    rec.current?.abort();
    const r = new Ctor();
    r.continuous = true;
    r.interimResults = true;
    const lead = typed && !/\s$/.test(typed) ? `${typed} ` : typed;
    r.onresult = (e) => {
      const said: string[] = [];
      for (let i = 0; i < e.results.length; i += 1) said.push(e.results[i][0].transcript);
      onText(`${lead}${said.join(' ').replace(/\s+/g, ' ').trim()}`);
    };
    r.onerror = (e) => {
      if (e.error === 'aborted' || e.error === 'no-speech') return;
      const denied = e.error === 'not-allowed' || e.error === 'service-not-allowed';
      trackFailed('group_dictation', denied ? 'mic_denied' : e.error || 'unknown');
      toast.error(denied ? 'We need microphone permission to hear you. Typing works just as well.' : 'Couldn’t hear that. Try again, or type it.');
    };
    r.onend = () => {
      if (rec.current !== r) return;
      rec.current = null;
      setListening(null);
    };
    try {
      r.start();
      rec.current = r;
      setListening(field);
    } catch (err) {
      trackFailed('group_dictation', 'start_failed', { error_detail: err instanceof Error ? err.message.slice(0, 120) : '' });
    }
  };
  const stop = (): void => { rec.current?.stop(); };
  return { supported: recognitionCtor() !== null, listening, start, stop };
}

const MIC = (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden>
    <rect x="9" y="3" width="6" height="11" rx="3" stroke="currentColor" strokeWidth="2" fill="none" />
    <path d="M5.5 11a6.5 6.5 0 0 0 13 0M12 17.5V21" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" />
  </svg>
);
export const MIC_SMALL = (
  <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden>
    <rect x="9" y="3" width="6" height="11" rx="3" stroke="currentColor" strokeWidth="1.8" fill="none" />
    <path d="M5.5 11a6.5 6.5 0 0 0 13 0M12 17.5V21" stroke="currentColor" strokeWidth="1.8" fill="none" strokeLinecap="round" />
  </svg>
);

/**
 * One field to write in: its label, a line of help (or a little guide card), the box (display serif for a question), the
 * mic in its corner, and the reason it was refused under it.
 */
export function GroupField({
  id, label, hint, guide, value, onChange, dictation, serif = false, rows, maxLength, placeholder, disabled = false, refused = '', fieldRef,
}: {
  id: string;
  label: string;
  hint?: string;
  guide?: ReactNode;
  value: string;
  onChange: (value: string) => void;
  dictation: Dictation;
  serif?: boolean;
  rows: number;
  maxLength: number;
  placeholder?: string;
  disabled?: boolean;
  refused?: string;
  fieldRef?: Ref<HTMLTextAreaElement>;
}): ReactElement {
  const on = dictation.listening === id;
  const box = useRef<HTMLTextAreaElement | null>(null);
  // Words that arrive by the mic, a draft or a suggestion grow the box as typing does.
  useEffect(() => { fitHeight(box.current); }, [value]);
  const setRefs = (el: HTMLTextAreaElement | null): void => {
    box.current = el;
    if (typeof fieldRef === 'function') fieldRef(el);
    else if (fieldRef) (fieldRef as { current: HTMLTextAreaElement | null }).current = el;
  };
  return (
    <div className="gr-field">
      <label className="gr-field-label" htmlFor={id}>{label}</label>
      {hint && <span className="gr-field-hint" id={`${id}-hint`}>{hint}</span>}
      {guide}
      <div className="gr-field-box">
        <textarea
          id={id}
          ref={setRefs}
          className={`gr-field-input${serif ? ' is-serif' : ''}${on ? ' is-listening' : ''}`}
          value={value}
          rows={rows}
          maxLength={maxLength}
          placeholder={placeholder}
          disabled={disabled}
          dir="auto"
          aria-describedby={[hint ? `${id}-hint` : '', refused ? `${id}-refused` : ''].filter(Boolean).join(' ') || undefined}
          aria-invalid={Boolean(refused)}
          onChange={(e) => { if (on) dictation.stop(); onChange(e.target.value); }}
        />
        {dictation.supported && !disabled && (
          <button
            type="button"
            className={`gr-mic${on ? ' is-on' : ''}`}
            aria-label={on ? 'Stop listening' : `Say it instead (${label})`}
            aria-pressed={on}
            data-log="group_mic"
            data-log-state={on ? 'stop' : 'start'}
            onClick={() => (on ? dictation.stop() : dictation.start(id, value, onChange))}
          >
            {MIC}
          </button>
        )}
      </div>
      {on && <span className="gr-field-listening" role="status">Listening… tap the mic when you’re done.</span>}
      {refused && <p id={`${id}-refused`} className="gr-refused" role="alert">{refused}</p>}
    </div>
  );
}

// ───────────────────────── when the words don't go up ─────────────────────────

/** Why a post or an answer did not go up: held back for the writer's own sake, one field refused, or a note. */
export type Refusal = { held: string } | { field: string; message: string } | { note: string };

/** `fields` names each field as the page says it ("Your question"). */
export function refusalOf(err: unknown, fields: Record<string, string>): Refusal {
  const code = apiErrorCode(err);
  if (code === 'risk_to_self') {
    const body = err instanceof ApiError ? (err.body as { resources?: string } | null) : null;
    return { held: body?.resources || '' };
  }
  const words = wordsRefused(err, fields);
  if (words) return words.field && fields[words.field] ? { field: words.field, message: words.message } : { note: words.message };
  if (code === 'too_many') return { note: 'That’s a lot for one day. Try again tomorrow.' };
  if (code === 'check_unavailable') return { note: 'Ember couldn’t take a look just now. Try again in a moment.' };
  if (code === 'not_open') return { note: 'This group isn’t open to you anymore.' };
  if (code === 'post_not_found') return { note: 'This post isn’t here anymore.' };
  if (code === 'answer_not_found') return { note: 'That answer isn’t here anymore.' };
  return { note: 'Couldn’t do that just now. Try again?' };
}

/** Words that carried a risk to the writer: they stay with them, and the lines to call are right here. */
export function HeldNote({ resources, what }: { resources: string; what: 'post' | 'answer' }): ReactElement {
  return (
    <div className="gr-held" role="alert" data-log-seen="group_held" data-log-state={what}>
      <p className="gr-held-title">This stays with you. It won’t be posted.</p>
      {resources && <p className="gr-held-copy">{resources}</p>}
      <Link to="/talk?mode=chat" className="gr-held-link" data-log="group_held_talk" data-log-state={what}>Talk to Ember</Link>
    </div>
  );
}

// ───────────────────────── Ember's look before it goes up ─────────────────────────

/** The parts of `text` that are a change's new words, marked. */
function marked(text: string, nows: string[]): ReactNode[] {
  const ranges: Array<[number, number]> = [];
  for (const now of nows) {
    if (!now) continue;
    for (let i = text.indexOf(now); i >= 0; i = text.indexOf(now, i + now.length)) ranges.push([i, i + now.length]);
  }
  ranges.sort((a, b) => a[0] - b[0]);
  const out: ReactNode[] = [];
  let at = 0;
  for (const [from, to] of ranges) {
    if (from < at) continue;
    if (from > at) out.push(text.slice(at, from));
    out.push(<mark key={from} className="gr-mark">{text.slice(from, to)}</mark>);
    at = to;
  }
  if (at < text.length) out.push(text.slice(at));
  return out;
}

const PERSON = (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden>
    <circle cx="12" cy="8" r="3.4" stroke="currentColor" strokeWidth="1.8" fill="none" />
    <path d="M5 20c1-3.6 3.8-5.4 7-5.4s6 1.8 7 5.4" stroke="currentColor" strokeWidth="1.8" fill="none" strokeLinecap="round" />
  </svg>
);
export const HEART = (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden>
    <path d="M12 20s-7-4.4-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.6-7 10-7 10z" stroke="currentColor" strokeWidth="1.8" fill="none" strokeLinejoin="round" />
  </svg>
);

/**
 * "Ember suggests a couple of changes": Ember's flame and "one small thing" on a warm band, the words as the check would
 * have them in one card (its new words marked), what each change was, and theirs to take — or to change themselves,
 * back in the form with the suggested words in it.
 */
export function CheckView({ kind, check, busy, notice, onTake, onEdit, reply = false }: {
  kind: GroupWritingKind;
  check: GroupCheck;
  busy: boolean;
  notice: string;
  onTake: () => void;
  onEdit: () => void;
  /** A reply under an answer (its button says so). */
  reply?: boolean;
}): ReactElement {
  const s = check.suggested ?? {};
  const nows = check.changes.map((c) => c.now);
  const n = check.changes.length;
  const parts: Array<{ label: string; text: string; serif: boolean }> = kind === 'question'
    ? [{ label: 'Your question', text: s.question ?? '', serif: true }, { label: 'Background', text: s.body ?? '', serif: false }]
    : kind === 'share' ? [{ label: 'Your post', text: s.body ?? '', serif: false }]
      : [{ label: reply ? 'Your reply' : 'Your answer', text: s.text ?? '', serif: false }];
  return (
    <main className="nf-page gr-page" data-log-seen="group_changes" data-log-state={reply ? 'reply' : kind}>
      <FriendsBar left={<button type="button" className="nf-iconbtn" aria-label="Back to your words" data-log="group_check_edit" data-log-state="back" onClick={onEdit}>{BACK_CHEVRON}</button>} />
      <section className="gr-band is-cream gr-check-band">
        <BandArt tone="cream" />
        <span className="gr-ember-face gr-check-flame" aria-hidden>
          <svg viewBox="0 0 24 24" width="26" height="26"><path d="M12 3c3 4.5 6 7 6 11a6 6 0 0 1-12 0c0-4 3-6.5 6-11z" fill="#fffaf2" /></svg>
        </span>
        <span className="gr-band-text">
          <span className="gr-band-hand">one small thing</span>
          <h1 className="gr-check-title">{n === 1 ? 'Ember suggests a small change' : n === 2 ? 'Ember suggests a couple of changes' : 'Ember suggests a few changes'}</h1>
        </span>
      </section>
      <div className="gr-check">
        <section className="gr-check-sheet">
          <span className="gr-check-hand">here’s how it would read</span>
          {parts.map((p) => (
            <div className="gr-check-part" key={p.label}>
              <span className="gr-check-label">{p.label}</span>
              <p className={`gr-check-text${p.serif ? ' is-serif' : ''}`} dir="auto">{marked(p.text, nows)}</p>
            </div>
          ))}
        </section>
        <ul className="gr-changes">
          {check.changes.map((c, i) => (
            <li className="gr-change" key={`${c.said}-${i}`}>
              <span className="gr-change-icon">{c.why === 'hurts' ? HEART : PERSON}</span>
              <span className="gr-change-text">
                {c.why === 'hurts' ? 'Softened a line about someone.' : 'Took out a name or detail, so no one can tell who it is.'}
                {' '}You said <s dir="auto">{c.said}</s>.
              </span>
            </li>
          ))}
        </ul>
      </div>
      <div className="nf-foot">
        {notice && <p className="nf-foot-note is-missing" role="alert">{notice}</p>}
        <button type="button" className="nf-cta is-block" disabled={busy} data-log="group_post" data-log-state={reply ? 'reply_suggested' : kind === 'answer' ? 'answer_suggested' : 'suggested'} onClick={onTake}>
          {reply ? 'Reply with this version' : kind === 'answer' ? 'Answer with this version' : 'Post this version'}
        </button>
        <button type="button" className="nf-text-btn" disabled={busy} data-log="group_check_edit" data-log-state="edit" onClick={onEdit}>Change it myself</button>
      </div>
    </main>
  );
}

// ───────────────────────── the warm bands ─────────────────────────

type Tone = 'peach' | 'sage' | 'cream';
const BAND: Record<Tone, { blob: string; ink: string; dot: string }> = {
  peach: { blob: '#F8D2B6', ink: '#B94B0A', dot: '#E2A23C' },
  sage: { blob: '#D2E1CD', ink: '#24603B', dot: '#E2A23C' },
  cream: { blob: '#EFDCB4', ink: '#8A3A07', dot: '#E2A23C' },
};

/** A band's soft shapes (the hub cards' language): a blob in the corner, a few dots, a little speech mark. */
export function BandArt({ tone, mark = 'none' }: { tone: Tone; mark?: 'question' | 'heart' | 'none' }): ReactElement {
  const c = BAND[tone];
  return (
    <svg className="gr-band-art" viewBox="0 0 358 100" preserveAspectRatio="xMaxYMid slice" aria-hidden>
      <circle cx="330" cy="8" r="58" fill={c.blob} />
      <circle cx="262" cy="90" r="4" fill="#C9B48A" />
      <circle cx="342" cy="88" r="7" fill={c.dot} />
      <circle cx="300" cy="94" r="3" fill={c.dot} />
      {mark !== 'none' && (
        <g>
          <rect x="268" y="34" width="58" height="40" rx="12" fill="#FFFDF8" />
          <path d="M286 74l-6 10 16-10z" fill="#FFFDF8" />
          {mark === 'question'
            ? <><path d="M292 49c0-5 9-5 9 1 0 4-5 4-5 9" stroke={c.ink} strokeWidth="2.4" fill="none" strokeLinecap="round" /><circle cx="296" cy="64" r="1.8" fill={c.ink} /></>
            : <path d="M297 66s-8-5-8-10.5a4.2 4.2 0 0 1 8-2.4 4.2 4.2 0 0 1 8 2.4C305 61 297 66 297 66z" fill={c.ink} />}
        </g>
      )}
    </svg>
  );
}

/** Who they are here, at the top of what they write: their face in the group, "Posting as <name>", and the group. */
export function PostingBand({ me, group, tone, mark }: { me: GroupFace; group: string; tone: Tone; mark: 'question' | 'heart' }): ReactElement {
  return (
    <section className={`gr-band is-${tone}`}>
      <BandArt tone={tone} mark={mark} />
      <GroupFaceView face={me} size={46} className="gr-band-face" />
      <span className="gr-band-text">
        <span className="gr-band-as">Posting as <strong dir="auto">{me.name}</strong></span>
        <span className="gr-band-group" dir="auto">{group}</span>
      </span>
    </section>
  );
}

// ───────────────────────── emoji ─────────────────────────

const SMILE_PLUS = (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden>
    <path d="M20.5 12a8.5 8.5 0 1 1-6-8.1" stroke="currentColor" strokeWidth="1.8" fill="none" strokeLinecap="round" />
    <path d="M8.5 14.2c.9 1.3 2.1 2 3.5 2s2.6-.7 3.5-2" stroke="currentColor" strokeWidth="1.8" fill="none" strokeLinecap="round" />
    <circle cx="9" cy="10" r="1.1" fill="currentColor" />
    <circle cx="15" cy="10" r="1.1" fill="currentColor" />
    <path d="M19 2.5v5M16.5 5h5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

/**
 * The emoji on a post or an answer (Groups 2026-11-03, in place of the hug): a capsule each — the emoji and how many,
 * theirs a shade warmer; a tap on theirs takes it off, on another makes that one theirs (one per person per item). The
 * small face opens the quick bar (the first `quick` of the set, More for all of it).
 */
export function GroupReactions({ reactions, emoji, onReact, groupId, what }: {
  reactions: GroupReaction[];
  emoji: GroupEmoji | undefined;
  onReact: (emoji: string | null) => void;
  groupId: string;
  what: 'post' | 'answer' | 'reply';
}): ReactElement {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const mine = reactions.find((r) => r.mine)?.emoji ?? null;
  return (
    <div className="gr-reacts">
      {reactions.map((r) => (
        <button key={r.emoji} type="button" className={`gr-react${r.mine ? ' is-mine' : ''}`} aria-pressed={r.mine}
          aria-label={`${r.emoji} ${r.count}${r.mine ? ', yours. Tap to take it off' : ''}`}
          data-log="group_react" data-log-state={r.mine ? 'removed' : 'capsule'} data-log-match-id={groupId}
          onClick={() => onReact(capsuleChoice(r))}>
          <span aria-hidden>{r.emoji}</span><span className="gr-react-n">{r.count}</span>
        </button>
      ))}
      {emoji && emoji.all.length > 0 && (
        <button type="button" className="gr-react-add" aria-label={`React to this ${what}`} aria-haspopup="menu"
          data-log="group_react_open" data-log-state={what} data-log-match-id={groupId} onClick={(e) => setAnchor(e.currentTarget)}>
          {SMILE_PLUS}
        </button>
      )}
      {anchor && emoji && (
        <RoomReactionBar anchor={anchor} set={emoji.all} quick={emoji.quick} mine={mine} roomId={groupId}
          label={`React to this ${what}`} moreLog="group_react_more" onClose={() => setAnchor(null)}
          onPick={(e, from) => {
            track('ui.clicked', { name: 'group_react', state: mine === e ? 'removed' : from, match_id: groupId });
            onReact(mine === e ? null : e);
          }} />
      )}
    </div>
  );
}

/** The flame on a tint, for a group's tile. */
export const GROUP_TINTS: Array<[string, string]> = [['#E2EDF4', '#3D4A5C'], ['#FBE1CF', '#8A3A07'], ['#E3ECE0', '#24603B'], ['#ECE6F6', '#5B4A8A']];

/** A group's tile: a few people around one speech mark, in the group's tint. */
export function GroupGlyph({ ink, size = 24 }: { ink: string; size?: number }): ReactElement {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden>
      <circle cx="8" cy="8" r="3" stroke={ink} strokeWidth="1.8" fill="none" />
      <circle cx="17" cy="11" r="2.4" stroke={ink} strokeWidth="1.8" fill="none" />
      <path d="M3 19c.8-3 2.8-4.6 5-4.6s4.2 1.6 5 4.6M14 19c.6-2.2 1.9-3.4 3.4-3.4" stroke={ink} strokeWidth="1.8" fill="none" strokeLinecap="round" />
    </svg>
  );
}

export const LOCK = (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden>
    <rect x="5" y="11" width="14" height="9" rx="2" stroke="currentColor" strokeWidth="1.8" fill="none" />
    <path d="M8 11V8a4 4 0 0 1 8 0v3" stroke="currentColor" strokeWidth="1.8" fill="none" />
  </svg>
);

export const MORE = (
  <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden>
    <circle cx="5.5" cy="12" r="1.7" fill="currentColor" />
    <circle cx="12" cy="12" r="1.7" fill="currentColor" />
    <circle cx="18.5" cy="12" r="1.7" fill="currentColor" />
  </svg>
);

export const CLOSE = (
  <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden>
    <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);
